"use client";

import { useFormContext, useWatch } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { StepId } from "@/features/multistep/registry/types";
import { useEffect, useState } from "react";
import { activityService } from "@/services/activityService";

export function FinancialInfoStep({ stepId }: { stepId: StepId }) {
  const { control, setValue } = useFormContext();

  const ingresosAnuales = useWatch({
    control,
    name: `${stepId}.ingresosAnuales`,
    defaultValue: 0,
  });

  const otrosIngresos = useWatch({
    control,
    name: `${stepId}.otrosIngresos`,
    defaultValue: 0,
  });

  const activos = useWatch({
    control,
    name: `${stepId}.activos`,
    defaultValue: 0,
  });

  const pasivos = useWatch({
    control,
    name: `${stepId}.pasivos`,
    defaultValue: 0,
  });

  useEffect(() => {
    // totalIngresos = ingresosAnuales + otrosIngresos
    setValue(`${stepId}.totalIngresos`, ingresosAnuales + otrosIngresos, {
      shouldValidate: true,
      shouldDirty: true,
    });

    // patrimonio = activos - pasivos
    setValue(`${stepId}.patrimonio`, activos - pasivos, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [ingresosAnuales, otrosIngresos, activos, pasivos, stepId]);

  return (
    <div className="space-y-6">
      {/* Fila 1: Actividad económica + Código CIIU */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mb-3">
        <FormField
          control={control}
          name={`${stepId}.actividadEconomica`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ocupación / oficio / profesión</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Ej: Comercio al por menor" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${stepId}.codigoCiiu`}
          render={({ field }) => {
            const [open, setOpen] = useState(false);
            const [loading, setLoading] = useState(false);
            const [options, setOptions] = useState<
              { value: string; label: string }[]
            >([]);
            const [query, setQuery] = useState("");

            const selected = options.find((opt) => opt.value === field.value);

            // 🔎 Buscar en servidor (con debounce)
            useEffect(() => {
              if (!query || query.length < 3) return; // mínimo 2 caracteres
              setLoading(true);

              const timeout = setTimeout(async () => {
                try {
                  const data = await activityService.getAll({ search: query });
                  setOptions(data); // [{ value, label }]
                } finally {
                  setLoading(false);
                }
              }, 300);

              return () => clearTimeout(timeout);
            }, [query]);

            useEffect(() => {
              if (options.length === 0) {
                (async () => {
                  setLoading(true);
                  try {
                    const data = await activityService.getAll(); // sin search = traer todo o paginado
                    setOptions(data);
                  } finally {
                    setLoading(false);
                  }
                })();
              }
            }, [options.length]);

            return (
              <FormItem>
                <FormLabel>Código CIIU</FormLabel>
                <FormControl>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between"
                      >
                        {selected
                          ? `${selected.value} - ${selected.label}`
                          : "Seleccione un código"}
                        <ChevronsUpDown className="opacity-50 ml-2 h-4 w-4 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0">
                      <Command>
                        <CommandInput
                          placeholder="Buscar código CIIU..."
                          className="h-9"
                          onValueChange={setQuery} // 🚀 lo que escribe el usuario
                        />
                        <CommandList>
                          {loading ? (
                            <CommandEmpty>Buscando...</CommandEmpty>
                          ) : options.length === 0 ? (
                            <CommandEmpty>
                              No se encontró ningún código.
                            </CommandEmpty>
                          ) : (
                            <CommandGroup>
                              {options.map((opt) => (
                                <CommandItem
                                  key={opt.value}
                                  value={`${opt.value} ${opt.label}`}
                                  onSelect={() => {
                                    field.onChange(opt.value);
                                    setOpen(false);
                                  }}
                                >
                                  {opt.value} - {opt.label}
                                  <Check
                                    className={cn(
                                      "ml-auto h-4 w-4",
                                      field.value === opt.value
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>

      {/* Fila 2: Campos monetarios */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-x-6 mb-3">
        {[
          {
            name: "ingresosAnuales",
            label: "Ingresos anuales",
            disabled: false,
          },
          { name: "otrosIngresos", label: "Otros ingresos", disabled: false },
          { name: "totalIngresos", label: "Total ingresos", disabled: true },
          { name: "activos", label: "Activos", disabled: false },
          { name: "pasivos", label: "Pasivos", disabled: false },
          { name: "patrimonio", label: "Patrimonio", disabled: true },
        ].map(({ name, label, disabled }, index) => (
          <FormField
            key={`${index}.${name}`}
            control={control}
            name={`${stepId}.${name}`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="number"
                      className="pl-7"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      disabled={disabled}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
      </div>

      {/* Fila 3: Concepto otros ingresos */}
      <FormField
        control={control}
        name={`${stepId}.conceptoOtrosIngresos`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Concepto otros ingresos</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="Ej: Venta de activos, donaciones..."
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
